import {
  CircularProgress,
  Divider,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { Help } from '@material-ui/icons';
import {
  Column,
  Columns,
  InfoCard,
  Inline,
  Stack,
  useResponsiveValue,
  useSnackbarStack,
  useUID,
} from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { useEffect } from 'react';
import { QueryUpdater } from 'shared/helpers/RoutingHelpers';
import { useCarrierInfo } from 'shared/modules/carrier-profile/data/CarrierInfoAPI';
import { ShipperRatingDTO } from 'shared/modules/ratings/data/ShipperRatingDTO';
import {
  useShipperRatings,
  useShipperRatingStatistics,
} from 'shared/modules/ratings/data/ShipperRatingsAPI';
import { EmptyRatings } from 'shared/modules/ratings/EmptyRatings';
import { RatingsPagination } from 'shared/modules/ratings/RatingsPagination';
import { ShipperProfileDTO } from 'shared/modules/shipper/ShipperProfileDTO';
import {
  RatingSortOption,
  ShipperProfilePageParams,
  SHIPPER_RATINGS_PAGE_SIZE,
} from 'shared/modules/shipper/ShipperProfilePageParams';
import { WebViewLink } from 'shared/routing/WebViewLinks';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { URLSearchQuery, URLSearchQueryInit } from 'shared/utils/URLUtils';
import { ShipperRating, ShipperRatingsList } from './ShipperRatingsList';
import { ShipperRatingStatistics } from './ShipperRatingStatistics';

const RATING_SORT_OPTIONS: Array<[RatingSortOption, string]> = [
  ['newest', 'Newest'],
  ['positive-ratings', 'Positive Ratings'],
  ['negative-ratings', 'Negative Ratings'],
];

interface RatingSortSelectProps {
  sort: RatingSortOption;
  onSortChange: (sort: RatingSortOption) => void;
}

function RatingsSortSelect({ sort, onSortChange }: RatingSortSelectProps) {
  const isMobile = useResponsiveValue(true, false);
  return (
    <TextField
      onChange={(event) => {
        onSortChange(event.target.value as RatingSortOption);
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Typography variant="body1" color="textSecondary">
              Sort by
            </Typography>
          </InputAdornment>
        ),
      }}
      defaultValue={sort}
      fullWidth={isMobile}
      select={true}
    >
      {RATING_SORT_OPTIONS.map(([value, label]) => (
        <MenuItem key={value} value={value}>
          {label}
        </MenuItem>
      ))}
    </TextField>
  );
}

interface Props {
  latestShipperRating?: ShipperRatingDTO;
  profile: ShipperProfileDTO;
  params: ShipperProfilePageParams;
  updateParams: QueryUpdater<URLSearchQuery, URLSearchQueryInit>;
  onClickRate: () => void;
  onClickDeleteRating: () => void;
}

export function ShipperProfileRatings({
  latestShipperRating,
  params,
  profile,
  updateParams,
  onClickRate,
  onClickDeleteRating,
}: Props) {
  const latestRatingUID = useUID();
  const ratingsListUID = useUID();
  const { addSnackbar } = useSnackbarStack();
  const isMobile = useResponsiveValue(true, false);

  const { data: carrierSettings } = useCarrierSettings();
  const isRateAllowed =
    carrierSettings?.carrier.verification_status === 'approved';

  const { data: carrierInfo } = useCarrierInfo();
  const { data: shipperRatingStatistics, error: shipperRatingStatisticsError } =
    useShipperRatingStatistics(profile.guid);
  const { data: shipperRatingsData, error: shipperRatingsError } =
    useShipperRatings(profile.guid, params);

  const hasShipperRatings = !!shipperRatingsData?.pagination.count;
  const hasLatestShipperRating = latestShipperRating?.guid;
  const maxPage = shipperRatingsData?.pagination.count
    ? Math.ceil(shipperRatingsData.pagination.count / SHIPPER_RATINGS_PAGE_SIZE)
    : 0;

  useEffect(() => {
    if (shipperRatingStatisticsError || shipperRatingsError) {
      addSnackbar('Failed to fetch shipper ratings', { variant: 'error' });
    }
  }, [addSnackbar, shipperRatingsError, shipperRatingStatisticsError]);

  return (
    <InfoCard size="large">
      <Stack space="small" align="center">
        <Columns space="small" align={['top', 'center']} collapseBelow="tablet">
          <Column width="content">
            <Typography variant="h3">Ratings Received</Typography>
          </Column>
          {!isMobile && (
            <Column width="content">
              <RatingsSortSelect
                sort={params.sort}
                onSortChange={(sort) => {
                  updateParams({ sort });
                }}
              />
            </Column>
          )}

          <Column width="fluid">
            <Inline
              space="xxsmall"
              horizontalAlign={['left', 'right']}
              verticalAlign="center"
              noWrap={true}
            >
              <Help fontSize="small" color="action" />

              <WebViewLink
                href="https://support.superdispatch.com/hc/articles/35661788289555-Ratings-in-Super-Dispatch"
                target="_blank"
                rel="noreferrer"
              >
                How ratings work?
              </WebViewLink>
            </Inline>
          </Column>

          {isMobile && (
            <Column width="content">
              <RatingsSortSelect
                sort={params.sort}
                onSortChange={(sort) => {
                  updateParams({ sort });
                }}
              />
            </Column>
          )}
        </Columns>

        {shipperRatingStatistics && shipperRatingsData ? (
          hasShipperRatings || hasLatestShipperRating ? (
            <Stack space="medium" align="center">
              <ShipperRatingStatistics data={shipperRatingStatistics} />

              <Stack space="large">
                {hasLatestShipperRating && (
                  <>
                    <Stack space="small" aria-labelledby={latestRatingUID}>
                      <Typography
                        id={latestRatingUID}
                        variant="h4"
                        color="textSecondary"
                      >
                        Rating Given by You
                      </Typography>

                      <ShipperRating
                        carrierName={carrierInfo?.name}
                        rating={latestShipperRating}
                        onClickEdit={onClickRate}
                        onClickDelete={onClickDeleteRating}
                        shipperProfile={profile}
                      />
                    </Stack>

                    {hasShipperRatings && (
                      <Box width="100%">
                        <Divider orientation="horizontal" />
                      </Box>
                    )}
                  </>
                )}

                {hasShipperRatings && (
                  <Stack space="small" aria-labelledby={ratingsListUID}>
                    <Typography
                      id={ratingsListUID}
                      variant="h4"
                      color="textSecondary"
                    >
                      Ratings by Carriers
                    </Typography>

                    <ShipperRatingsList
                      ratings={shipperRatingsData.data}
                      shipperProfile={profile}
                    />

                    {maxPage > 1 && (
                      <RatingsPagination
                        page={params.page}
                        count={maxPage}
                        onChange={(_, pageNumber) => {
                          updateParams({ ...params, page: pageNumber });
                        }}
                      />
                    )}
                  </Stack>
                )}
              </Stack>
            </Stack>
          ) : (
            <EmptyRatings
              message={
                isRateAllowed && (
                  <Box marginTop="small">
                    <Button onClick={onClickRate}>Rate Shipper</Button>
                  </Box>
                )
              }
            />
          )
        ) : (
          <Box paddingTop="xxlarge" height="172px">
            <CircularProgress />
          </Box>
        )}
      </Stack>
    </InfoCard>
  );
}
